export const getStatusText = (typeId, DEVICE_TYPE) => {
  if (typeId === DEVICE_TYPE.Ready) return "Ready";
  if (typeId === DEVICE_TYPE.Out_for_Delivery) return "Out for Delivery";
  if (typeId === DEVICE_TYPE.Arrived) return "Arrived";
  if (typeId === DEVICE_TYPE.Stocked) return "Stocked";
  if (typeId === DEVICE_TYPE.Return) return "Return";
  if (typeId === DEVICE_TYPE.Cancelled) return "Cancelled";
  if (typeId === DEVICE_TYPE.Completed) return "Completed";
  if (typeId === DEVICE_TYPE.Hold) return "On Hold";
  return "";
};

const hasParkingSpotFilled = (order) => {
  const label = order && order.parking_spot_label;
  return !!(label && String(label).trim());
};

export const compareOrders = (a = {}, b = {}, DEVICE_TYPE, options = {}) => {
  // Helper function to get order priority
  // Lower number = higher priority (appears first)
  const getOrderPriority = (order) => {
    const isArrived = order && order.type_id === DEVICE_TYPE.Arrived;
    const isPaid = order && (order.is_paid === true || order.is_payroll_deduct === true);
    
    if (isArrived && isPaid) {
      return 1; // Highest priority: Arrived + Paid/PayLater
    }
    if (isArrived) {
      return 2; // Second priority: Arrived (not paid)
    }
    if (isPaid) {
      return 3; // Third priority: Paid/PayLater (not arrived)
    }
    return 4; // Lowest priority: Other orders
  };

  const priorityA = getOrderPriority(a);
  const priorityB = getOrderPriority(b);

  // Compare by priority first
  if (priorityA !== priorityB) {
    return priorityA - priorityB; // Lower priority number comes first
  }

  // Curbside: within the same priority band, Arrived rows with a parking
  // spot (or "No spot available") float above Arrived without arrival data.
  if (options && options.isCurbside && priorityA <= 2) {
    const aHasSpot = hasParkingSpotFilled(a);
    const bHasSpot = hasParkingSpotFilled(b);
    if (aHasSpot !== bHasSpot) {
      return aHasSpot ? -1 : 1;
    }
  }

  // If same priority, maintain original order (stable sort)
  return 0;
};

export const calculateTotalDueAmount = (popupData) => {
  if (
    !popupData ||
    !popupData.items ||
    !Array.isArray(popupData.items) ||
    popupData.items.length === 0
  ) {
    return 0;
  }

  return popupData.items.reduce((sum, current) => {
    const price = parseFloat(current.price);
    if (!isNaN(price)) {
      return sum + price;
    }
    return sum;
  }, 0);
};

export const formatCurrency = (amount = 0) => {
  const numericAmount = Number.isFinite(amount) ? amount : 0;
  return `$${numericAmount.toFixed(2)}`;
};

/**
 * Parse a currency-ish value ("$2.50", 2.5, "2.50") into a finite number.
 */
const parseMoney = (value) => {
  if (value === undefined || value === null || value === "") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : NaN;
};

/**
 * Resolve a single order's payment breakdown (total / paid / due) including
 * partial-payment support. Prefers the server-provided `amount` object (which
 * now carries paidAmount / dueAmount / isPartiallyPaid), and falls back to
 * summing line items when amount data is absent.
 */
export const getOrderPaymentBreakdown = (orderData = {}) => {
  const amount = orderData && orderData.amount;
  const itemsTotal = calculateTotalDueAmount(orderData);

  let total =
    (amount && parseMoney(amount.Total)) || (Number.isFinite(itemsTotal) ? itemsTotal : 0);
  if (!Number.isFinite(total)) total = 0;

  const isPaid =
    (amount && amount.isPaid === true) || orderData.is_paid === true;

  if (isPaid) {
    return {
      total: Number(total.toFixed(2)),
      paid: Number(total.toFixed(2)),
      due: 0,
      isPaid: true,
      isPartiallyPaid: false,
    };
  }

  // Partial payment: trust explicit amount fields when present.
  const explicitPaid = amount ? parseMoney(amount.paidAmount) : NaN;
  const explicitDue = amount ? parseMoney(amount.dueAmount) : NaN;
  const explicitPaidFromLabel = amount ? parseMoney(amount.Paid) : NaN;
  const explicitDueFromLabel = amount ? parseMoney(amount.Due) : NaN;

  let paid = Number.isFinite(explicitPaid)
    ? explicitPaid
    : Number.isFinite(explicitPaidFromLabel)
      ? explicitPaidFromLabel
      : 0;
  let due = Number.isFinite(explicitDue)
    ? explicitDue
    : Number.isFinite(explicitDueFromLabel)
      ? explicitDueFromLabel
      : Math.max(0, Number((total - paid).toFixed(2)));

  paid = Math.max(0, Math.min(total, paid));
  due = Math.max(0, Math.min(total, due));

  const isPartiallyPaid =
    (amount && amount.isPartiallyPaid === true) || (paid > 0 && due > 0);

  return {
    total: Number(total.toFixed(2)),
    paid: Number(paid.toFixed(2)),
    due: Number(due.toFixed(2)),
    isPaid: false,
    isPartiallyPaid,
  };
};





// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/helpers/orderUtils.js